import { memo } from "react";
import { gameToScreen, type Camera, type ProjectionConfig } from "@/engine/projection.ts";
import type { Route } from "@/session/schemas.ts";
import type { GameCoord } from "@/engine/coords.ts";

interface RouteLayerProps {
  readonly routes: readonly Route[];
  /** Waypoints of the route currently being drawn — not yet committed. */
  readonly pendingWaypoints?: readonly GameCoord[] | undefined;
  readonly camera: Camera;
  readonly config: ProjectionConfig;
  readonly myId: string;
  readonly canManageAll: boolean;
  readonly onRemove: (routeId: string) => void;
}

const ARROW_SIZE = 10;

function arrowHeadPoints(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const a1 = angle + Math.PI * 0.8;
  const a2 = angle - Math.PI * 0.8;
  return [
    `${to.x},${to.y}`,
    `${to.x + ARROW_SIZE * Math.cos(a1)},${to.y + ARROW_SIZE * Math.sin(a1)}`,
    `${to.x + ARROW_SIZE * Math.cos(a2)},${to.y + ARROW_SIZE * Math.sin(a2)}`,
  ].join(" ");
}

/**
 * Flight paths / movement routes, drawn as directional arrows.
 *
 * Each route is a polyline with an arrowhead at its end, so a pilot or
 * driver can see where to go and in which direction. Clicking the line
 * removes it (same pattern as markers — one tap placed it, one tap clears
 * it), with the same permission model: you can remove your own, the
 * commander can remove anyone's.
 */
function RouteLayerImpl({
  routes,
  pendingWaypoints,
  camera,
  config,
  myId,
  canManageAll,
  onRemove,
}: RouteLayerProps): React.ReactElement {
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full">
      <defs>
        <marker
          id="arrow-friendly"
          viewBox="0 0 10 10"
          refX={10}
          refY={5}
          markerWidth={8}
          markerHeight={8}
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-accent)" />
        </marker>
      </defs>

      {routes.map((route) => {
        const points = route.waypoints.map((wp) => gameToScreen(wp, camera, config));
        if (points.length < 2) return null;
        const canRemove = canManageAll || route.createdBy === myId;
        const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
        const last = points[points.length - 1]!;
        const prev = points[points.length - 2]!;

        return (
          <g key={route.id}>
            {/* Thick invisible hit area for tap-to-remove. */}
            {canRemove && (
              <path
                d={d}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                className="pointer-events-auto cursor-pointer"
                data-no-pan=""
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(route.id);
                }}
              />
            )}
            <path
              d={d}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity={0.85}
            />
            <polygon
              points={arrowHeadPoints(prev, last)}
              fill="var(--color-accent)"
              fillOpacity={0.85}
            />
          </g>
        );
      })}

      {/* The route being drawn right now — no arrowhead yet, just a preview. */}
      {pendingWaypoints &&
        pendingWaypoints.length >= 2 &&
        (() => {
          const points = pendingWaypoints.map((wp) => gameToScreen(wp, camera, config));
          const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
          const last = points[points.length - 1]!;
          const prev = points[points.length - 2]!;
          return (
            <g>
              <path
                d={d}
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="6 4"
                strokeOpacity={0.7}
              />
              <polygon
                points={arrowHeadPoints(prev, last)}
                fill="var(--color-accent)"
                fillOpacity={0.7}
              />
            </g>
          );
        })()}
    </svg>
  );
}

export const RouteLayer = memo(RouteLayerImpl);
